/** Mirrors backend ValidationLimits.PortraitMaxBytes (5 MB). */
export const PORTRAIT_MAX_BYTES = 5 * 1024 * 1024
export const PORTRAIT_SIZE = 512

export class PortraitReadError extends Error {}

/** The largest centred square inside a w×h image. */
export function centerSquare(w: number, h: number) {
  const side = Math.min(w, h)
  return { sx: Math.round((w - side) / 2), sy: Math.round((h - side) / 2), side }
}

/**
 * [Canvasser redesign Phase 7, P7-6] Centre-crop to a square and downscale to ≤512px JPEG. Lives in lib/
 * (not components/canvasser) because canvas encoding is on the canvasser subtree's no-export ban list.
 * The photo goes only to the canvasser's own grant endpoint.
 */
export async function toPortraitJpeg(file: Blob, size = PORTRAIT_SIZE): Promise<Blob> {
  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })
  try {
    const { sx, sy, side } = centerSquare(bitmap.width, bitmap.height)
    const out = Math.min(size, side)
    const canvas = document.createElement('canvas')
    canvas.width = out
    canvas.height = out
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('canvas unavailable')
    ctx.drawImage(bitmap, sx, sy, side, side, 0, 0, out, out)
    return await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob(b => (b ? resolve(b) : reject(new Error('encode failed'))), 'image/jpeg', 0.85))
  } finally {
    bitmap.close()
  }
}

/** Processed JPEG when the browser can decode the file. Otherwise the original file when it fits the cap
 *  (the server sniffs it and returns a readable 400 if it isn't an image). Otherwise PortraitReadError. */
export async function preparePortrait(file: File): Promise<Blob> {
  try {
    return await toPortraitJpeg(file)
  } catch {
    if (file.size > 0 && file.size <= PORTRAIT_MAX_BYTES) return file
    throw new PortraitReadError()
  }
}
